const parseEnumToken = (value) => {
  if (typeof value !== 'string') {
    return '';
  }

  return value.trim();
};

const createEnumHelpers = (values) => {
  const valueSet = new Set(values);

  const normalize = (value) => {
    const token = parseEnumToken(value);
    return valueSet.has(token) ? token : '';
  };

  return {
    values,
    normalize,
    matches: (value, expected) => normalize(value) === expected,
  };
};

export const PLATFORM_ROLES = Object.freeze({
  SUPER_ADMIN: 'SUPER_ADMIN',
  SOCIETY_ADMIN: 'SOCIETY_ADMIN',
  SOCIETY_MANAGER: 'SOCIETY_MANAGER',
  SOCIETY_GUARD: 'SOCIETY_GUARD',
  ACCOUNT_MANAGER: 'ACCOUNT_MANAGER',
  DEVELOPER: 'DEVELOPER',
  SUPPORT_AGENT: 'SUPPORT_AGENT',
  SUPPORT_MANAGER: 'SUPPORT_MANAGER',
  TECHNICIAN: 'TECHNICIAN',
  INSTALLATION_MANAGER: 'INSTALLATION_MANAGER',
  AUDITOR: 'AUDITOR',
  MOBILE_USER: 'MOBILE_USER',
});

export const MANAGER_ACCESS_LEVELS = Object.freeze({
  FULL_ACCESS: 'FULL_ACCESS',
  READ_ONLY: 'READ_ONLY',
});

export const TAG_STATUSES = Object.freeze({
  AVAILABLE: 'AVAILABLE',
  ALLOCATED: 'ALLOCATED',
  ASSIGNED: 'ASSIGNED',
  INACTIVE: 'INACTIVE',
  BLOCKED: 'BLOCKED',
  LOST: 'LOST',
  DAMAGED: 'DAMAGED',
});

export const USER_TYPES = Object.freeze({
  RESIDENT: 'RESIDENT',
  TENANT: 'TENANT',
  VISITOR: 'VISITOR',
  THIRDPARTY: 'THIRDPARTY',
});

export const USER_ONBOARDING_STATUSES = Object.freeze({
  INCOMPLETE_ONBOARDING: 'INCOMPLETE_ONBOARDING',
  ACTIVE: 'ACTIVE',
});

export const GENDERS = Object.freeze({
  MALE: 'MALE',
  FEMALE: 'FEMALE',
  OTHER: 'OTHER',
});

export const VEHICLE_TYPES = Object.freeze({
  CAR: 'CAR',
  BIKE: 'BIKE',
  SCOOTER: 'SCOOTER',
  BUS: 'BUS',
  TRUCK: 'TRUCK',
  OTHER: 'OTHER',
});

export const INVENTORY_VEHICLE_TYPES = Object.freeze({
  TWO_WHEELER: 'TWO_WHEELER',
  FOUR_WHEELER: 'FOUR_WHEELER',
});

export const VEHICLE_CATEGORY_LABELS = Object.freeze({
  [INVENTORY_VEHICLE_TYPES.TWO_WHEELER]: 'Two-wheeler',
  [INVENTORY_VEHICLE_TYPES.FOUR_WHEELER]: 'Four-wheeler',
});

export const VEHICLE_CATEGORY_FILTER_OPTIONS = Object.freeze([
  {
    label: VEHICLE_CATEGORY_LABELS[INVENTORY_VEHICLE_TYPES.TWO_WHEELER],
    value: INVENTORY_VEHICLE_TYPES.TWO_WHEELER,
  },
  {
    label: VEHICLE_CATEGORY_LABELS[INVENTORY_VEHICLE_TYPES.FOUR_WHEELER],
    value: INVENTORY_VEHICLE_TYPES.FOUR_WHEELER,
  },
]);

export const CONTROLLER_STATUSES = Object.freeze({
  ONLINE: 'ONLINE',
  OFFLINE: 'OFFLINE',
});

export const DEVICE_TYPES = Object.freeze({
  CONTROLLER: 'CONTROLLER',
});

export const CONTROLLER_LOG_TYPES = Object.freeze({
  BOTH: 'BOTH',
  ENTRY: 'ENTRY',
  EXIT: 'EXIT',
});

export const READER_TYPES = Object.freeze({
  UHF: 'UHF',
});

export const LOG_EVENT_TYPES = Object.freeze({
  ENTRY: 'ENTRY',
  EXIT: 'EXIT',
  LOCKED: 'LOCKED',
});

export const PARKING_ENTRY_STATUSES = Object.freeze({
  PARKED: 'PARKED',
  EXITED: 'EXITED',
});

export const VEHICLE_TAG_TYPES = Object.freeze({
  PARKTEK: 'PARKTEK',
  FASTAG: 'FASTAG',
});

export const CHAT_MESSAGE_TYPES = Object.freeze({
  TEXT: 'TEXT',
});

const roleHelpers = createEnumHelpers(Object.values(PLATFORM_ROLES));
const managerAccessHelpers = createEnumHelpers(Object.values(MANAGER_ACCESS_LEVELS));
const tagStatusHelpers = createEnumHelpers(Object.values(TAG_STATUSES));
const userTypeHelpers = createEnumHelpers(Object.values(USER_TYPES));
const userOnboardingStatusHelpers = createEnumHelpers(Object.values(USER_ONBOARDING_STATUSES));
const genderHelpers = createEnumHelpers(Object.values(GENDERS));
const vehicleTypeHelpers = createEnumHelpers(Object.values(VEHICLE_TYPES));
const inventoryVehicleTypeHelpers = createEnumHelpers(Object.values(INVENTORY_VEHICLE_TYPES));
const controllerStatusHelpers = createEnumHelpers(Object.values(CONTROLLER_STATUSES));
const controllerLogTypeHelpers = createEnumHelpers(Object.values(CONTROLLER_LOG_TYPES));
const logEventTypeHelpers = createEnumHelpers(Object.values(LOG_EVENT_TYPES));
const parkingEntryStatusHelpers = createEnumHelpers(Object.values(PARKING_ENTRY_STATUSES));
const vehicleTagTypeHelpers = createEnumHelpers(Object.values(VEHICLE_TAG_TYPES));
const chatMessageTypeHelpers = createEnumHelpers(Object.values(CHAT_MESSAGE_TYPES));

export const normalizePlatformRole = roleHelpers.normalize;
export const normalizeManagerAccessLevel = managerAccessHelpers.normalize;
export const normalizeTagStatus = tagStatusHelpers.normalize;
export const normalizeUserType = userTypeHelpers.normalize;
export const normalizeUserOnboardingStatus = userOnboardingStatusHelpers.normalize;
export const normalizeGender = genderHelpers.normalize;
export const normalizeVehicleType = vehicleTypeHelpers.normalize;
export const normalizeInventoryVehicleType = inventoryVehicleTypeHelpers.normalize;
export const normalizeControllerStatus = controllerStatusHelpers.normalize;
export const normalizeControllerLogType = controllerLogTypeHelpers.normalize;
export const normalizeLogEventType = logEventTypeHelpers.normalize;
export const normalizeParkingEntryStatus = parkingEntryStatusHelpers.normalize;
export const normalizeVehicleTagType = vehicleTagTypeHelpers.normalize;
export const normalizeChatMessageType = chatMessageTypeHelpers.normalize;

export const toApiVehicleType = (value) => vehicleTypeHelpers.normalize(value) || '';
export const toApiInventoryVehicleType = (value) => inventoryVehicleTypeHelpers.normalize(value) || '';

export const getInventoryVehicleTypeForValue = (value) => {
  const inventoryType = inventoryVehicleTypeHelpers.normalize(value);
  if (inventoryType) {
    return inventoryType;
  }

  const vehicleType = vehicleTypeHelpers.normalize(value);
  if (vehicleType === VEHICLE_TYPES.BIKE || vehicleType === VEHICLE_TYPES.SCOOTER) {
    return INVENTORY_VEHICLE_TYPES.TWO_WHEELER;
  }

  if (
    vehicleType === VEHICLE_TYPES.CAR ||
    vehicleType === VEHICLE_TYPES.BUS ||
    vehicleType === VEHICLE_TYPES.TRUCK ||
    vehicleType === VEHICLE_TYPES.OTHER
  ) {
    return INVENTORY_VEHICLE_TYPES.FOUR_WHEELER;
  }

  return '';
};

export const formatEnumLabel = (value) =>
  parseEnumToken(value)
    .split('_')
    .filter(Boolean)
    .map((token) => token.charAt(0) + token.slice(1).toLowerCase())
    .join(' ');
